/**
 * Perplexity API Service
 *
 * Provides AI-powered features with real-time web search for fetching trending topics
 * for content creation.
 */

const PERPLEXITY_API_KEY = process.env.REACT_APP_PERPLEXITY_API_KEY;

/**
 * Fetch trending topics using Perplexity's Sonar model with real-time web search
 *
 * Returns top 5 trending/breaking topics in tech/AI that would make
 * great blog posts, with title, description, and context for content generation.
 *
 * @returns {Promise<Array>} Array of trending topic objects
 */
export const fetchTrendingTopics = async () => {
  if (!PERPLEXITY_API_KEY) {
    throw new Error(
      "Perplexity API key not configured. Add REACT_APP_PERPLEXITY_API_KEY to .env.local"
    );
  }

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const response = await fetch("https://api.perplexity.ai/chat/completions", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${PERPLEXITY_API_KEY}`,
    },
    body: JSON.stringify({
      model: "sonar",
      messages: [
        {
          role: "system",
          content: `You are an AI industry analyst and content strategist with access to real-time web search. Today is ${today}. 

Your task is to search the entire web and identify the TOP 5 most relevant trending topics, breaking news, or discussion-worthy subjects in AI across technology and business sectors that would make excellent blog posts.

Focus on AI-related topics including:
- AI model releases, updates, and breakthroughs (LLMs, computer vision, multimodal AI, etc.)
- Enterprise AI adoption and business applications
- AI regulations, policy, and ethics discussions
- AI infrastructure, tools, and development platforms
- AI startups, funding rounds, and market movements
- AI research papers and scientific breakthroughs
- AI impact on industries (finance, healthcare, education, etc.)
- Hot discussions in the AI community (Twitter/X, Reddit, HackerNews, LinkedIn)
- AI controversies, debates, or paradigm shifts

Search broadly across authoritative sources including tech news sites, business publications, research institutions, company blogs, and social media discussions. Prioritize content from the past 24-48 hours that is generating significant engagement.

Return a JSON object with a "topics" array containing exactly 5 items. Each item must have:
- "title": A compelling, specific headline (not generic)
- "description": 2-3 sentences explaining why this is trending and why it matters to the AI community
- "context": 3-4 sentences with key facts, stats, implications, or angles to explore in a blog post
- "source_hint": Brief note on where this news originated (e.g., "ArXiv preprint", "Company announcement", "Industry discussion", "Regulatory filing")

Be specific with real, current events from your web search. Focus on high-impact AI news and discussions happening right now.`,
        },
        {
          role: "user",
          content:
            "Search the web broadly for the top 5 trending AI topics across technology and business right now that would make great blog posts. Give me specific, timely topics that are being discussed today. Return the result as a JSON object with a 'topics' array.",
        },
      ],
      temperature: 0.7,
      max_tokens: 2000,
      return_citations: true,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || `Perplexity API error: ${response.status}`);
  }

  const data = await response.json();
  const content = data.choices[0]?.message?.content;

  if (!content) {
    throw new Error("No response from Perplexity");
  }

  try {
    // Try to extract JSON from the response
    let parsed;

    // If the response is wrapped in code blocks, extract it
    const jsonMatch = content.match(/```(?:json)?\s*(\{[\s\S]*\})\s*```/);
    if (jsonMatch) {
      parsed = JSON.parse(jsonMatch[1]);
    } else {
      // Try parsing directly
      parsed = JSON.parse(content);
    }

    return parsed.topics || [];
  } catch (e) {
    console.error("Failed to parse Perplexity response:", content);
    throw new Error("Failed to parse trending topics response");
  }
};

export default {
  fetchTrendingTopics,
};
